import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { FooterComponent } from '../footer/footer.component';
import { AuthHeaderComponent } from '../auth-header/auth-header.component';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, FooterComponent, AuthHeaderComponent],
  template: `
    <main>
      <app-auth-header></app-auth-header>

      <div class="forgot-password-scroll">
      <div class="forgot-password-container">
        <h1 class="form-title">Passwort zurücksetzen</h1>
        
        <!-- STEP 1: Enter Email and New Password -->
        @if (currentStep() === 'enter-password') {
          <form [formGroup]="passwordForm" (ngSubmit)="onRequestReset()">
            <div class="form-group">
              <label for="email">E-Mail:</label>
              <input
                id="email"
                type="email"
                formControlName="email"
                placeholder="E-Mail-Adresse"
              />
              @if (passwordForm.get('email')?.hasError('required') && passwordForm.get('email')?.touched) {
                <span class="error">E-Mail ist erforderlich</span>
              }
              @if (passwordForm.get('email')?.hasError('email') && passwordForm.get('email')?.touched) {
                <span class="error">Ungültige E-Mail</span>
              }
            </div>

            <div class="form-group">
              <label for="pwd">Neues Passwort:</label>
              <div class="password-wrapper">
                <input
                  id="pwd"
                  [type]="showPwd() ? 'text' : 'password'"
                  formControlName="pwd"
                  placeholder="Passwort (mind. 6 Zeichen)"
                />
                <button
                  type="button"
                  class="toggle-password-btn"
                  (click)="showPwd.set(!showPwd())"
                  tabindex="-1"
                  [attr.aria-label]="showPwd() ? 'Passwort verbergen' : 'Passwort anzeigen'"
                >
                  {{ showPwd() ? '👁️‍🗨️' : '👁️' }}
                </button>
              </div>
              @if (passwordForm.get('pwd')?.hasError('required') && passwordForm.get('pwd')?.touched) {
                <span class="error">Passwort ist erforderlich</span>
              }
              @if (passwordForm.get('pwd')?.hasError('minlength') && passwordForm.get('pwd')?.touched) {
                <span class="error">Passwort muss mind. 6 Zeichen lang sein</span>
              }
            </div>

            <div class="form-group">
              <label for="pwdConfirm">Passwort wiederholen:</label>
              <div class="password-wrapper">
                <input
                  id="pwdConfirm"
                  [type]="showPwdConfirm() ? 'text' : 'password'"
                  formControlName="pwdConfirm"
                  placeholder="Passwort wiederholen"
                />
                <button
                  type="button"
                  class="toggle-password-btn"
                  (click)="showPwdConfirm.set(!showPwdConfirm())"
                  tabindex="-1"
                  [attr.aria-label]="showPwdConfirm() ? 'Passwort verbergen' : 'Passwort anzeigen'"
                >
                  {{ showPwdConfirm() ? '👁️‍🗨️' : '👁️' }}
                </button>
              </div>
              @if (passwordForm.get('pwdConfirm')?.hasError('required') && passwordForm.get('pwdConfirm')?.touched) {
                <span class="error">Passwort wiederholen ist erforderlich</span>
              }
              @if (passwordForm.get('pwdConfirm')?.hasError('minlength') && passwordForm.get('pwdConfirm')?.touched) {
                <span class="error">Passwort muss mind. 6 Zeichen lang sein</span>
              }
              @if (!passwordsMatch() && (passwordForm.get('pwd')?.touched || passwordForm.get('pwdConfirm')?.touched)) {
                <span class="error">Passwörter stimmen nicht überein</span>
              }
            </div>

            @if (authService.errorMessage()) {
              <div class="error-message">
                {{ authService.errorMessage() }}
              </div>
            }

            <button type="submit" [disabled]="!passwordForm.valid || isLoading() || !passwordsMatch()">
              @if (isLoading()) {
                Wird versendet...
              } @else {
                Passwort zurücksetzen
              }
            </button>
          </form>

          <p class="login-link">
            <a routerLink="/login">Zurück zum Login</a>
          </p>
        }

        <!-- STEP 2: Verify Code -->
        @if (currentStep() === 'verify-code') {
          <p class="info-text">
            Wir haben einen Verifizierungscode an <strong>{{ userEmail }}</strong> gesendet.
            Bitte geben Sie den Code ein, um Ihr Passwort zurückzusetzen.
          </p>

          <form [formGroup]="codeForm" (ngSubmit)="onConfirmReset()">
            <div class="form-group">
              <label for="code">Verifizierungscode:</label>
              <input
                id="code"
                type="text"
                formControlName="code"
                placeholder="6-stelliger Code"
                maxlength="6"
                (input)="onCodeInput($event)"
              />
              @if (codeForm.get('code')?.hasError('required') && codeForm.get('code')?.touched) {
                <span class="error">Code ist erforderlich</span>
              }
              @if (codeForm.get('code')?.hasError('minlength') && codeForm.get('code')?.touched) {
                <span class="error">Code muss 6 Ziffern enthalten</span>
              }
            </div>

            @if (authService.errorMessage()) {
              <div class="error-message">
                {{ authService.errorMessage() }}
              </div>
            }

            @if (resetSuccess()) {
              <div class="success-message">
                Passwort erfolgreich geändert! Sie werden zum Login weitergeleitet...
              </div>
            }

            @if (!resetSuccess()) {
              <button type="submit" [disabled]="!codeForm.valid || isLoading()">
                @if (isLoading()) {
                  Passwort wird aktualisiert...
                } @else {
                  Passwort aktualisieren
                }
              </button>
            }
          </form>

          <div class="resend-section">
            <p>Code nicht erhalten?</p>
            <button 
              type="button" 
              (click)="onResendCode()"
              [disabled]="isResendLoading() || resendCooldown() > 0"
              class="resend-button"
            >
              @if (isResendLoading()) {
                Wird versendet...
              } @else if (resendCooldown() > 0) {
                Erneut versenden in {{ resendCooldown() }}s
              } @else {
                Code erneut versenden
              }
            </button>
            <button
              type="button"
              (click)="goBack()"
              class="back-button"
            >
              Zurück
            </button>
          </div>

          <p class="login-link">
            <a routerLink="/login">Zurück zum Login</a>
          </p>
        }
      </div>
      </div>
    </main>

    <app-footer></app-footer>
  `,
  styles: [`
    :host {
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        Helvetica, Arial, sans-serif;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: hidden;
    }

    main {
      flex: 1;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 0;
      min-height: 0;
      box-sizing: border-box;
    }

    .forgot-password-scroll {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      padding: 22px 2rem 1.5rem;
      box-sizing: border-box;
    }

    main > div:not(.header-container) {
      padding: 0 2rem;
    }

    main > div:first-child {
      padding: 0;
    }

    .forgot-password-container {
      max-width: 480px !important;
      margin: auto;
      padding: 40px;
      border: 1px solid #ccc;
      border-radius: 8px;
      background: #f9f9f9;
      width: 100%;
      box-sizing: border-box;
    }

    h1 {
      text-align: center;
      margin-bottom: 30px;
      color: #333;
    }

    .info-text {
      text-align: center;
      color: #666;
      margin-bottom: 30px;
      margin-top: -5px;
      font-size: 14px;
    }

    .info-text strong {
      color: #333;
    }

    .form-group {
      margin-bottom: 20px;
      display: flex;
      flex-direction: column;
    }

    label {
      margin-bottom: 5px;
      color: #555;
      font-weight: 500;
    }

    input {
      padding: 10px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
      font-family: inherit;
    }

    input:focus {
      outline: none;
      border-color: #4CAF50;
      box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
    }

    .password-wrapper {
      position: relative;
      display: flex;
      align-items: center;
    }

    .password-wrapper input {
      width: 100%;
      padding-right: 35px;
    }

    .password-wrapper input::-webkit-credentials-auto-fill-button,
    .password-wrapper input::-webkit-outer-spin-button,
    .password-wrapper input::-webkit-inner-spin-button {
      display: none;
    }

    .password-wrapper input::-ms-reveal {
      display: none;
    }

    .toggle-password-btn {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      cursor: pointer;
      font-size: 16px;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #666;
    }

    .toggle-password-btn:hover {
      background-color: white;
      border-radius: 4px;
    }

    .toggle-password-btn:focus {
      outline: none;
    }

    .error {
      color: #d32f2f;
      font-size: 12px;
      margin-top: 5px;
    }

    .error-message {
      background-color: #ffebee;
      color: #d32f2f;
      padding: 10px;
      border-radius: 4px;
      margin-bottom: 15px;
      border-left: 4px solid #d32f2f;
    }

    .success-message {
      background-color: #e8f5e9;
      color: #2e7d32;
      padding: 10px;
      border-radius: 4px;
      margin-bottom: 15px;
      border-left: 4px solid #2e7d32;
    }

    button {
      width: 100%;
      padding: 10px;
      background-color: #4CAF50;
      color: white;
      border: none;
      border-radius: 4px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.3s;
    }

    button:not(.toggle-password-btn):hover:not(:disabled) {
      background-color: #45a049;
    }

    button:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
    }

    .resend-section {
      margin-top: 15px;
      padding-top: 15px;
      border-top: 1px solid #ddd;
      text-align: center;
    }

    .resend-section p {
      color: #666;
      margin-bottom: 10px;
      font-size: 14px;
    }

    .resend-button {
      background-color: #2196F3;
      font-size: 14px;
      margin-bottom: 10px;
    }

    .resend-button:hover:not(:disabled) {
      background-color: #0b7dda;
    }

    .back-button {
      background-color: #666;
      font-size: 14px;
    }

    .back-button:hover:not(:disabled) {
      background-color: #555;
    }

    .login-link {
      text-align: center;
      margin-top: 20px;
      font-size: 14px;
    }

    .login-link a {
      color: #4CAF50;
      text-decoration: none;
      font-weight: 500;
    }

    .login-link a:hover {
      text-decoration: underline;
    }

    .form-title {
      text-align: center;
      margin-bottom: 30px;
      color: #333;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ForgotPasswordComponent {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  protected authService = inject(AuthService);

  currentStep = signal<'enter-password' | 'verify-code'>('enter-password');
  showPwd = signal(false);
  showPwdConfirm = signal(false);
  isLoading = signal(false);
  isResendLoading = signal(false);
  resendCooldown = signal(0);
  resetSuccess = signal(false);
  passwordsMatch = signal(true);
  
  userEmail = '';
  storedPassword = '';

  passwordForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    pwd: ['', [Validators.required, Validators.minLength(6)]],
    pwdConfirm: ['', [Validators.required, Validators.minLength(6)]]
  });

  codeForm = this.fb.group({
    code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
  });

  constructor() {
    this.authService.errorMessage.set(null);
    
    // Watch password fields
    this.passwordForm.get('pwd')?.valueChanges.subscribe(() => this.checkPasswords());
    this.passwordForm.get('pwdConfirm')?.valueChanges.subscribe(() => this.checkPasswords());
  }

  private checkPasswords() {
    const pwd = this.passwordForm.get('pwd')?.value || '';
    const pwdConfirm = this.passwordForm.get('pwdConfirm')?.value || '';
    this.passwordsMatch.set(pwd === pwdConfirm);
  }

  onRequestReset() {
    if (this.passwordForm.valid && this.passwordsMatch()) {
      const { email, pwd, pwdConfirm } = this.passwordForm.value;

      if (pwd !== pwdConfirm) {
        return;
      }

      if (email && pwd) {
        this.isLoading.set(true);
        this.authService.requestPasswordReset(email, pwd).subscribe({
          next: () => {
            this.isLoading.set(false);
            this.userEmail = email;
            this.storedPassword = pwd;
            
            // Store password temporarily for confirmation step
            sessionStorage.setItem('resetPassword', pwd);
            sessionStorage.setItem('resetEmail', email);

            // Move to step 2
            this.currentStep.set('verify-code');
            this.authService.errorMessage.set(null);
          },
          error: (err) => {
            this.isLoading.set(false);
          }
        });
      }
    }
  }

  onConfirmReset() {
    if (this.codeForm.valid) {
      const { code } = this.codeForm.value;
      const email = sessionStorage.getItem('resetEmail') || this.userEmail;
      const password = sessionStorage.getItem('resetPassword') || this.storedPassword;

      if (code && email && password) {
        this.isLoading.set(true);
        this.authService.confirmPasswordReset(email, password, code).subscribe({
          next: () => {
            this.isLoading.set(false);
            this.resetSuccess.set(true);
            sessionStorage.removeItem('resetPassword');
            sessionStorage.removeItem('resetEmail');

            setTimeout(() => {
              this.router.navigate(['/login']);
            }, 2000);
          },
          error: (err) => {
            this.isLoading.set(false);
          }
        });
      }
    }
  }

  onResendCode() {
    const email = sessionStorage.getItem('resetEmail') || this.userEmail;
    const password = sessionStorage.getItem('resetPassword') || this.storedPassword;

    if (email && password) {
      this.isResendLoading.set(true);
      this.authService.requestPasswordReset(email, password).subscribe({
        next: () => {
          this.isResendLoading.set(false);
          this.resendCooldown.set(60);
          const interval = setInterval(() => {
            this.resendCooldown.update(val => {
              if (val <= 1) {
                clearInterval(interval);
                return 0;
              }
              return val - 1;
            });
          }, 1000);
          this.authService.errorMessage.set(null);
        },
        error: (err) => {
          this.isResendLoading.set(false);
        }
      });
    }
  }

  goBack() {
    this.currentStep.set('enter-password');
    this.codeForm.reset();
    this.authService.errorMessage.set(null);
  }

  onCodeInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleanValue = input.value.replace(/\s/g, '');
    if (cleanValue !== input.value) {
      input.value = cleanValue;
      this.codeForm.get('code')?.setValue(cleanValue, { emitEvent: false });
    }
  }
}
